import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Spinner from './Spinner';
import { streamTruckEvents } from '../utils/streamingApi';
import AQIModal from './AQIModal';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TextField } from "@mui/material";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    BarController,
    LineController
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { ReactComponent as JustAirLogo } from '../61520fa346e5a67b68fd5039_justair-logo-full-color-rgb-2.svg';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    BarController,
    LineController,
    Title,
    Tooltip,
    Legend
);

const getAQIColor = (aqi) => {
    if (aqi === undefined || aqi === null) return { color: 'gray-400', label: 'No Data' };
    if (aqi <= 50) return { color: 'green-500', label: 'Good' };
    if (aqi <= 100) return { color: 'yellow-400', label: 'Moderate' };
    if (aqi <= 150) return { color: 'orange-500', label: 'Unhealthy for Sensitive Groups' };
    if (aqi <= 200) return { color: 'red-500', label: 'Unhealthy' };
    if (aqi <= 300) return { color: 'purple-600', label: 'Very Unhealthy' };
    return { color: 'rose-900', label: 'Hazardous' };
};

const formatEventId = (eventId, eagleEyeObjectId, timestamp) => {
    try {
        // Extract date components
        const date = new Date(timestamp);
        const year = date.getFullYear().toString().slice(-2);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const hour = date.getHours().toString().padStart(2, '0');
        const minute = date.getMinutes().toString().padStart(2, '0');
        
        // Use Eagle Eye object ID if available, otherwise fallback to event ID
        const uniqueId = eagleEyeObjectId ? eagleEyeObjectId.slice(-4) : eventId.slice(-4);
        
        // Format: TRK-YYMMDD-HHMM-XXXX
        return `TRK-${year}${month}${day}-${hour}${minute}-${uniqueId}`;
    } catch (error) {
        console.error('Error formatting event ID:', error);
        return eventId; // Fallback to original ID if formatting fails
    }
};

const AQIIndicator = ({ aqi }) => {
    const { color } = getAQIColor(aqi);
    const value = aqi || 'N/A';
    
    if (!aqi) {
        return (
            <div className="flex flex-col space-y-2 py-1">
                <div className="flex items-center gap-3">
                    <div className="flex-1 relative h-3">
                        <div className="absolute inset-0 rounded-full bg-gray-200"></div>
                    </div>
                </div>
                <div className="flex justify-center items-center px-1">
                    <span className="text-sm text-gray-500 font-medium">No Data Available</span>
                </div>
            </div>
        );
    }
    
    return (
        <div className="flex flex-col space-y-2 py-1">
            <div className="flex items-center gap-3">
                <div className="flex-1 relative h-3">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-green-500 via-yellow-400 via-orange-500 via-red-500 via-purple-600 to-rose-900"></div>
                    <div 
                        className={`absolute h-5 w-5 -top-1 rounded-full bg-${color} border-2 border-white shadow-lg transform -translate-y-[10%]`}
                        style={{ left: `${Math.min((aqi / 500) * 100, 100)}%`, transform: 'translateX(-50%)' }}
                    >
                        <div className={`absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-${color} text-white text-xs font-bold px-2 py-0.5 rounded-md min-w-[40px] text-center`}>
                            {value}
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex justify-between items-center px-1">
                <span className="text-[10px] text-green-600 font-medium">Good</span>
                <span className="text-[10px] text-yellow-500 font-medium">Moderate</span>
                <span className="text-[10px] text-orange-500 font-medium">USG</span>
                <span className="text-[10px] text-red-500 font-medium">Unhealthy</span>
                <span className="text-[10px] text-purple-600 font-medium">Very</span>
                <span className="text-[10px] text-rose-900 font-medium">Hazard</span>
            </div>
        </div>
    );
};

const JobList = () => {
    const [cameras, setCameras] = useState([]);
    const [selectedCameraId, setSelectedCameraId] = useState("");
    const [truckData, setTruckData] = useState([]);
    const [snapshots, setSnapshots] = useState({});
    const [visibleSnapshots, setVisibleSnapshots] = useState({});
    const [filter, setFilter] = useState({ status: '', startDate: '', endDate: '', eventType: 'all' });
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentEvent, setCurrentEvent] = useState(null);
    const [previousEvent, setPreviousEvent] = useState(null);
    const [viewMode, setViewMode] = useState('daily');
    const [aggregatedData, setAggregatedData] = useState(null);
    const [expandedPollutants, setExpandedPollutants] = useState({});
    const [loadingSnapshots, setLoadingSnapshots] = useState({});
    const [errorStates, setErrorStates] = useState({});
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [timeSinceLastRefresh, setTimeSinceLastRefresh] = useState(0);
    const [darkMode, setDarkMode] = useState(false);
    const [dateRangePreset, setDateRangePreset] = useState('30');
    const [compareMode, setCompareMode] = useState(false);
    const [selectedCompareRange, setSelectedCompareRange] = useState(null); // eslint-disable-line no-unused-vars
    const [customStartDate, setCustomStartDate] = useState(null);
    const [customEndDate, setCustomEndDate] = useState(null);
    const [customDateError, setCustomDateError] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hotspotClicks, setHotspotClicks] = useState(0);
    const [cameraMenuOpen, setCameraMenuOpen] = useState(false);
    const [dateRangeMenuOpen, setDateRangeMenuOpen] = useState(false);
    const [eventFilterMenuOpen, setEventFilterMenuOpen] = useState(false);
    const [showTokenInfo, setShowTokenInfo] = useState(false);
    const [tokenInfo, setTokenInfo] = useState(null);
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [eventsPerPage] = useState(50);
    const [dataRangeWarning, setDataRangeWarning] = useState(null);
    const [progress, setProgress] = useState(0);
    const [streamingCancelRef, setStreamingCancelRef] = useState(null);

    const dateRangePresets = [
        { value: 'today', label: 'Today Only' },
        { value: '24h', label: 'Last 24 Hours' },
        { value: '72h', label: 'Last 72 Hours' },
        { value: 'week', label: 'Last 7 Days' },
        { value: 'month', label: 'Last 30 Days' },
        { value: 'custom', label: 'Custom Range' }
    ];

    const validateCustomDateRange = (startDate, endDate) => {
        if (!startDate || !endDate) {
            return "Please select both start and end dates";
        }
        
        if (startDate >= endDate) {
            return "Start date must be before end date";
        }
        
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        
        if (startDate < oneYearAgo) {
            return "Start date cannot be more than 1 year ago";
        }
        
        const now = new Date();
        if (endDate > now) {
            return "End date cannot be in the future";
        }
        
        return null;
    };

    const handleCustomDateChange = (startDate, endDate) => {
        setCustomStartDate(startDate);
        setCustomEndDate(endDate);
        
        const error = validateCustomDateRange(startDate, endDate);
        setCustomDateError(error);
    };

    // Clear custom dates when switching away from custom range
    useEffect(() => {
        if (dateRangePreset !== 'custom') {
            setCustomStartDate(null);
            setCustomEndDate(null);
            setCustomDateError(null);
        }
    }, [dateRangePreset]);

    useEffect(() => {
        const fetchCameras = async () => {
            try {
                console.log('Fetching cameras from:', `${process.env.REACT_APP_API_URL}/api/cameras`);
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/cameras`);
                console.log('Camera response received:', {
                    status: response.status,
                    data: response.data,
                    hasResults: !!response.data?.results,
                    resultsLength: response.data?.results?.length || 0
                });
                
                if (response.data?.results) {
                    setCameras(response.data.results);
                } else {
                    console.error('Invalid camera response format:', response.data);
                    setErrorStates(prev => ({ ...prev, fetchCameras: 'Invalid camera data format received' }));
                }
            } catch (error) {
                console.error('Error fetching cameras:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                    statusText: error.response?.statusText
                });
                setErrorStates(prev => ({ ...prev, fetchCameras: 'Failed to fetch cameras' }));
            }
        };
        fetchCameras();
    }, []);

    // Cleanup streaming connection on component unmount
    useEffect(() => {
        return () => {
            if (streamingCancelRef) {
                streamingCancelRef();
                setStreamingCancelRef(null);
            }
        };
    }, [streamingCancelRef]);

    const aggregateData = React.useCallback((data, mode) => {
        if (!data.length) return null;

        const aggregated = {};
        
        const endDate = new Date();
        const startDate = new Date();
        
        // Determine the time range based on dateRangePreset
        switch (dateRangePreset) {
            case '1':
                if (mode === 'hourly') {
                    startDate.setHours(endDate.getHours() - 24, 0, 0, 0);
                    endDate.setMinutes(59, 59, 999);
                } else {
                    startDate.setDate(endDate.getDate() - 1);
                    startDate.setHours(0, 0, 0, 0);
                    endDate.setHours(23, 59, 59, 999);
                }
                break;
            case '30':
                startDate.setDate(endDate.getDate() - 29);
                    startDate.setHours(0, 0, 0, 0);
                    endDate.setHours(23, 59, 59, 999);
                // For 30 days, force daily view to prevent overcrowding
                if (mode === 'hourly') mode = 'daily';
                break;
            case '14':
                startDate.setDate(endDate.getDate() - 13);
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                // For 14 days, force daily view to prevent overcrowding
                if (mode === 'hourly') mode = 'daily';
                break;
            case 'today':
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'march1':
                startDate.setFullYear(2025, 2, 1);
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                // For long ranges, force daily view
                if (mode === 'hourly') mode = 'daily';
                break;
            default:
                const timestamps = data.map(event => new Date(event.startTimestamp).getTime());
                startDate.setTime(Math.min(...timestamps));
                endDate.setTime(Math.max(...timestamps));
                if (mode === 'hourly') {
                    startDate.setMinutes(0, 0, 0);
                    endDate.setMinutes(59, 59, 999);
                } else {
                    startDate.setHours(0, 0, 0, 0);
                    endDate.setHours(23, 59, 59, 999);
                }
        }

        let currentDate = new Date(startDate);
        while (currentDate <= endDate) {
            let key;
            const nextDate = new Date(currentDate);
            
            switch (mode) {
                case 'hourly':
                    key = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')} ${String(currentDate.getHours()).padStart(2, '0')}:00`;
                    nextDate.setHours(currentDate.getHours() + 1);
                    break;
                case 'daily':
                    key = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
                    nextDate.setDate(currentDate.getDate() + 1);
                    break;
                case 'weekly':
                    const weekStart = new Date(currentDate);
                    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
                    key = `Week of ${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
                    nextDate.setDate(currentDate.getDate() + 7);
                    break;
                case 'monthly':
                    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                    key = `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
                    nextDate.setMonth(currentDate.getMonth() + 1);
                    break;
                default:
                    return null;
            }

            aggregated[key] = {
                uniqueEvents: new Set(),
                avgAQI: 0,
                totalAQI: 0,
                aqiCount: 0
            };
            
            currentDate = nextDate;
        }

        data.forEach(event => {
            const eventDate = new Date(event.startTimestamp);
            
            if (eventDate < startDate || eventDate > endDate) return;
            
            let key;
            switch (mode) {
                case 'hourly':
                    key = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')} ${String(eventDate.getHours()).padStart(2, '0')}:00`;
                    break;
                case 'daily':
                    key = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}`;
                    break;
                case 'weekly':
                    const weekStart = new Date(eventDate);
                    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
                    key = `Week of ${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
                    break;
                case 'monthly':
                    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                                             'July', 'August', 'September', 'October', 'November', 'December'];
                    key = `${monthNames[eventDate.getMonth()]} ${eventDate.getFullYear()}`;
                    break;
                default:
                    return;
            }

            if (!aggregated[key]) {
                aggregated[key] = {
                    uniqueEvents: new Set(),
                    avgAQI: 0,
                    totalAQI: 0,
                    aqiCount: 0
                };
            }

            if (!event.isAQIOnly) {
                aggregated[key].uniqueEvents.add(event.id);
            }
            
            if (event.aqiData?.aqi) {
                aggregated[key].totalAQI += event.aqiData.aqi;
                aggregated[key].aqiCount++;
                aggregated[key].avgAQI = aggregated[key].totalAQI / aggregated[key].aqiCount;
            }
        });

        const formattedData = Object.entries(aggregated).reduce((acc, [key, value]) => {
            acc[key] = {
                uniqueTrucks: value.uniqueEvents.size,
                avgAQI: value.avgAQI || 0
            };
            return acc;
        }, {});

        return Object.keys(formattedData)
            .sort()
            .reduce((acc, key) => {
                acc[key] = formattedData[key];
                return acc;
            }, {});
    }, [dateRangePreset]);

    useEffect(() => {
        const loadMoreEvents = async () => {
            if (isLoadingMore || !hasMore) return;
            
            setIsLoadingMore(true);
            try {
                const nextPage = currentPage + 1;
                let url = `${process.env.REACT_APP_API_URL}/api/trucks/${selectedCameraId}?page=${nextPage}&limit=${eventsPerPage}&sort=-startTimestamp`;
                
                if (filter.eventType !== 'all') {
                    url += `&eventType=${filter.eventType === 'truck' ? 'truckOnly' : 'aqiOnly'}`;
                }
                
                const response = await axios.get(url);
                const newEvents = response.data.results || [];
                
                if (newEvents.length > 0) {
                    setTruckData(prev => [...prev, ...newEvents]);
                    setCurrentPage(nextPage);
                    setHasMore(newEvents.length === eventsPerPage);
                } else {
                    setHasMore(false);
                }
            } catch (error) {
                console.error('Error loading more events:', error);
            } finally {
                setIsLoadingMore(false);
            }
        };

        const handleScroll = () => {
            if (viewMode === 'list' && !isLoadingMore && hasMore) {
                const scrollPosition = window.innerHeight + window.scrollY;
                const documentHeight = document.documentElement.scrollHeight;
                if (scrollPosition >= documentHeight - 100) {
                    loadMoreEvents();
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [viewMode, isLoadingMore, hasMore, currentPage, selectedCameraId, filter.eventType, eventsPerPage, truckData]);

    useEffect(() => {
        if (viewMode !== 'list' && truckData.length > 0) {
            const processChartData = (events, mode) => {
                if (!events || !Array.isArray(events)) return null;

                const filteredEvents = events.filter(event => {
                    if (filter.eventType === 'all') return true;
                    if (filter.eventType === 'truck') return !event.isAQIOnly;
                    if (filter.eventType === 'aqi') return event.isAQIOnly;
                    return true;
                });

                if (filteredEvents.length === 0) return null;

                // Sort events by timestamp
                filteredEvents.sort((a, b) => new Date(a.startTimestamp) - new Date(b.startTimestamp));

                const dataMap = new Map();
                
                // Get date range
                const startDate = new Date(Math.min(...filteredEvents.map(e => new Date(e.startTimestamp))));
                const endDate = new Date(Math.max(...filteredEvents.map(e => new Date(e.startTimestamp))));

                // Initialize current date at the start of the period
                let currentDate = new Date(startDate);
                currentDate.setHours(0, 0, 0, 0);

                // For weekly view, move to the start of the week
                if (mode === 'weekly') {
                    currentDate.setDate(currentDate.getDate() - currentDate.getDay());
                }
                // For monthly view, move to the start of the month
                else if (mode === 'monthly') {
                    currentDate.setDate(1);
                }

                // Generate time slots
                while (currentDate <= endDate) {
                    let key;
                    let nextDate = new Date(currentDate);
                    let periodStart = new Date(currentDate);
                    let periodEnd = new Date(nextDate);

                    switch (mode) {
                        case 'hourly':
                            key = currentDate.toISOString().slice(0, 13);
                            nextDate.setHours(currentDate.getHours() + 1);
                            periodEnd = nextDate;
                            break;
                        case 'daily':
                            key = currentDate.toISOString().split('T')[0];
                            nextDate.setDate(currentDate.getDate() + 1);
                            periodEnd = nextDate;
                            break;
                        case 'weekly':
                            const weekEnd = new Date(currentDate);
                            weekEnd.setDate(weekEnd.getDate() + 6);
                            weekEnd.setHours(23, 59, 59, 999);
                            key = `Week of ${currentDate.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
                            nextDate.setDate(currentDate.getDate() + 7);
                            periodEnd = new Date(weekEnd);
                            break;
                        case 'monthly':
                            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                                             'July', 'August', 'September', 'October', 'November', 'December'];
                            // Set to first day of month
                            periodStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                            // Set to last day of month
                            periodEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59, 999);
                            key = `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
                            nextDate.setMonth(currentDate.getMonth() + 1);
                            nextDate.setDate(1);
                            break;
                        default:
                            return null;
                    }

                    if (!dataMap.has(key)) {
                        dataMap.set(key, {
                            timestamp: key,
                            truckCount: 0,
                            aqiValues: [],
                            maxAQI: null,
                            avgAQI: null,
                            totalAQI: 0,
                            aqiCount: 0,
                            startDate: new Date(periodStart),
                            endDate: new Date(periodEnd)
                        });
                    }
                    
                    currentDate = nextDate;
                }

                // Process events
                filteredEvents.forEach(event => {
                    const eventDate = new Date(event.startTimestamp);
                    
                    // Find the correct time slot for the event
                    let targetSlot = null;
                    for (const [, slot] of dataMap.entries()) {
                        if (eventDate >= slot.startDate && eventDate <= slot.endDate) {
                            targetSlot = slot;
                            break;
                        }
                    }

                    if (targetSlot) {
                        // Increment truck count only for non-AQI events
                        if (!event.isAQIOnly) {
                            targetSlot.truckCount = (targetSlot.truckCount || 0) + 1;
                        }
                        
                        if (event.aqiData?.aqi) {
                            targetSlot.aqiValues.push(event.aqiData.aqi);
                            targetSlot.totalAQI += event.aqiData.aqi;
                            targetSlot.aqiCount++;
                            targetSlot.maxAQI = Math.max(...targetSlot.aqiValues);
                            targetSlot.avgAQI = targetSlot.totalAQI / targetSlot.aqiCount;
                        }
                    }
                });

                // Convert Map to array and sort
                return Array.from(dataMap.values())
                    .sort((a, b) => {
                        // Special handling for monthly view to ensure correct chronological order
                        if (mode === 'monthly') {
                            return a.startDate.getTime() - b.startDate.getTime();
                        }
                        return a.startDate - b.startDate;
                    })
                    .map(data => ({
                        timestamp: data.timestamp,
                        truckCount: data.truckCount || 0,
                        maxAQI: data.maxAQI,
                        avgAQI: data.avgAQI ? parseFloat(data.avgAQI.toFixed(1)) : null,
                        startDate: data.startDate // Keep this for potential debugging
                    }));
            };

            const processedData = processChartData(truckData, viewMode);
            setAggregatedData(processedData);
        }
    }, [truckData, viewMode, filter.eventType]);

    const memoizedFetchTruckData = React.useCallback((isAutoRefresh = false) => {
        if (!selectedCameraId) {
            alert("Please select a camera first.");
            return;
        }

        // Cancel any existing stream
        if (streamingCancelRef) {
            streamingCancelRef();
            setStreamingCancelRef(null);
        }

        if (!isAutoRefresh) {
            setLoading(true);
        }
        setIsRefreshing(true);
        setProgress(0);
        setTruckData([]);

        try {
            const currentDate = new Date();
            let startDate = new Date();
            let endDate = new Date();

            // Handle different date range presets
            switch (dateRangePreset) {
                case 'march1':
                    startDate = new Date('2025-03-01T00:00:00.000Z');
                    endDate = currentDate;
                    break;
                case 'today':
                    startDate.setHours(0, 0, 0, 0);
                    endDate = currentDate;
                    break;
                case '24h':
                    startDate.setHours(currentDate.getHours() - 24);
                    endDate = currentDate;
                    break;
                case '72h':
                    startDate.setHours(currentDate.getHours() - 72);
                    endDate = currentDate;
                    break;
                case 'week':
                    startDate.setDate(currentDate.getDate() - 7);
                    startDate.setHours(0, 0, 0, 0);
                    endDate = currentDate;
                    break;
                case 'month':
                    startDate.setDate(currentDate.getDate() - 30);
                    startDate.setHours(0, 0, 0, 0);
                    endDate = currentDate;
                    endDate.setHours(23, 59, 59, 999);
                    break;
                case 'custom':
                    if (customStartDate && customEndDate) {
                        startDate = new Date(customStartDate);
                        startDate.setHours(0, 0, 0, 0);
                        endDate = new Date(customEndDate);
                        endDate.setHours(23, 59, 59, 999);
                    } else {
                        startDate.setDate(currentDate.getDate() - 7);
                        startDate.setHours(0, 0, 0, 0);
                        endDate = currentDate;
                    }
                    break;
                default:
                    const days = parseInt(dateRangePreset);
                    if (!isNaN(days)) {
                        startDate.setDate(currentDate.getDate() - days);
                        startDate.setHours(0, 0, 0, 0);
                        endDate = currentDate;
                        endDate.setHours(23, 59, 59, 999);
                    }
            }

            // Determine parameters for streaming
            const dateRangeMs = endDate - startDate;
            const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
            const shouldUseDays = dateRangeMs <= oneWeekMs;
            
            const params = shouldUseDays 
                ? { days: Math.ceil(dateRangeMs / (24 * 60 * 60 * 1000)) }
                : { startDate: startDate.toISOString(), endDate: endDate.toISOString() };

            let allTruckEvents = [];
            let allAqiEvents = [];

            const cancelStream = streamTruckEvents(
                selectedCameraId,
                params,
                (data) => {
                    switch (data.type) {
                        case 'connection':
                            console.log('Connected to stream');
                            break;

                        case 'progress':
                            setProgress(parseFloat(data.data.progress));
                            break;

                        case 'truck_events':
                            allTruckEvents = [...allTruckEvents, ...data.data.events];
                            const combinedData = [...allTruckEvents, ...allAqiEvents];
                            
                            // Apply status filter if needed
                            let filteredData = combinedData;
                            if (filter.status) {
                                filteredData = filteredData.filter(event => {
                                    const { label } = getAQIColor(event.aqiData?.aqi);
                                    return label === filter.status;
                                });
                            }

                            const existingIds = new Set(truckData.map(event => event.id));
                            const newData = filteredData.map(event => ({
                                ...event,
                                isNew: !existingIds.has(event.id)
                            }));
                            
                            setTruckData(newData);
                            break;

                        case 'aqi_intervals':
                            allAqiEvents = [...allAqiEvents, ...data.data.events];
                            const allCombined = [...allTruckEvents, ...allAqiEvents];
                            
                            // Apply status filter if needed
                            let filteredCombined = allCombined;
                            if (filter.status) {
                                filteredCombined = filteredCombined.filter(event => {
                                    const { label } = getAQIColor(event.aqiData?.aqi);
                                    return label === filter.status;
                                });
                            }

                            const combinedExistingIds = new Set(truckData.map(event => event.id));
                            const combinedNewData = filteredCombined.map(event => ({
                                ...event,
                                isNew: !combinedExistingIds.has(event.id)
                            }));
                            
                            setTruckData(combinedNewData);
                            break;

                        case 'summary':
                            console.log('Stream summary:', data.data);
                            const finalData = [...allTruckEvents, ...allAqiEvents];
                            
                            // Final filtering and processing
                            let finalFiltered = finalData;
                            if (filter.status) {
                                finalFiltered = finalFiltered.filter(event => {
                                    const { label } = getAQIColor(event.aqiData?.aqi);
                                    return label === filter.status;
                                });
                            }

                            const finalExistingIds = new Set(truckData.map(event => event.id));
                            const finalProcessed = finalFiltered.map(event => ({
                                ...event,
                                isNew: !finalExistingIds.has(event.id)
                            }));

                            setTruckData(finalProcessed);

                            if (viewMode !== 'list') {
                                const newAggregatedData = aggregateData(finalProcessed, viewMode);
                                setAggregatedData(newAggregatedData);
                            }

                            console.log(`Received ${finalProcessed.length} events (${finalProcessed.filter(e => !e.isAQIOnly).length} truck events)`);
                            setLastRefresh(new Date());
                            setDataRangeWarning(null);
                            break;

                        default:
                            console.warn('Unknown stream data type:', data.type);
                            break;
                    }
                },
                () => {
                    setLoading(false);
                    setIsRefreshing(false);
                    setProgress(100);
                    setStreamingCancelRef(null);
                    console.log('Stream completed');
                },
                (error) => {
                    setLoading(false);
                    setIsRefreshing(false);
                    setStreamingCancelRef(null);
                    console.error('Stream error:', error);
                    setErrorStates(prev => ({ 
                        ...prev, 
                        fetchData: `Streaming error: ${error.message}` 
                    }));
                }
            );

            setStreamingCancelRef(() => cancelStream);

        } catch (error) {
            console.error('Setup error:', error);
            setErrorStates(prev => ({ 
                ...prev, 
                fetchData: `Setup error: ${error.message}` 
            }));
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [selectedCameraId, dateRangePreset, filter.status, truckData, viewMode, aggregateData, customStartDate, customEndDate, streamingCancelRef]);

    const fetchSnapshot = async (eventId, timestamp) => {
        try {
            if (snapshots[eventId]) {
                setTimeout(() => {
                    setVisibleSnapshots(prev => ({
                        ...prev,
                        [eventId]: !prev[eventId]
                    }));
                }, 50);
                return;
            }

            setLoadingSnapshots(prev => ({ ...prev, [eventId]: true }));
            
            const url = `${process.env.REACT_APP_API_URL}/api/trucks/${selectedCameraId}/recorded-image?timestamp=${encodeURIComponent(timestamp)}`;
            console.log('Fetching snapshot from:', url);
            
            const response = await axios.get(url, { 
                responseType: "blob",
                headers: {
                    "Accept": "image/jpeg"
                }
            });
            
            console.log('Snapshot response received:', {
                status: response.status,
                type: response.data.type,
                size: response.data.size
            });

            if (response.data.size === 0) {
                throw new Error('Received empty image data');
            }

            // Create a blob URL from the response data
            const blob = new Blob([response.data], { type: 'image/jpeg' });
            const imageUrl = URL.createObjectURL(blob);
            
            setTimeout(() => {
                setSnapshots(prev => ({ ...prev, [eventId]: imageUrl }));
                setVisibleSnapshots(prev => ({ ...prev, [eventId]: true }));
            }, 50);
        } catch (error) {
            console.error('Error fetching snapshot:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                url: `${process.env.REACT_APP_API_URL}/api/trucks/${selectedCameraId}/recorded-image?timestamp=${encodeURIComponent(timestamp)}`
            });
            setErrorStates(prev => ({ 
                ...prev, 
                [eventId]: `Failed to load snapshot: ${error.response?.status === 404 ? 'Image not found' : 'Server error'}`
            }));
        } finally {
            setLoadingSnapshots(prev => ({ ...prev, [eventId]: false }));
        }
    };

    const handleOpenModal = (index) => {
        if (index > 0) {
            setCurrentEvent(truckData[index]);
            setPreviousEvent(truckData[index - 1]);
            setIsModalOpen(true);
        } else {
            alert('No previous event to compare with.');
        }
    };

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        if (mode !== 'list' && truckData.length > 0) {
            const newAggregatedData = aggregateData(truckData, mode);
            setAggregatedData(newAggregatedData);
        }
    };

    const exportData = async (format) => {
        try {
            // Only export actual truck events & filters out hourly AQI interval entries 
            const dataToExport = truckData.filter(event => !event.isAQIOnly).map(event => ({
                'Event ID': event.id,
            'Eagle Eye Object ID': event.eagleEyeObjectId || 'N/A',
                'Timestamp': new Date(event.startTimestamp).toLocaleString(),
                'Camera': event.actorName,
                'AQI': event.aqiData?.aqi || 'N/A',
                'Monitor Info': event.monitorInfo?.code || 'N/A',
                'Distance': event.monitorInfo?.distance ? 
                    `${(parseFloat(event.monitorInfo.distance) * 0.000621371).toFixed(2)} miles` : 
                    'N/A',
                'PM2.5 (µg/m³)': event.aqiData?.pm25 || 'N/A',
                'NO₂ (PPB)': event.aqiData?.no2 || 'N/A',
                'PM10 (µg/m³)': event.aqiData?.pm10 || 'N/A'
            }));

            if (format === 'xlsx') {
                const wb = XLSX.utils.book_new();
                
                const ws = XLSX.utils.json_to_sheet(dataToExport);

                XLSX.utils.sheet_add_aoa(ws, [
                    ['Trucks Off Our Streets Dashboard'],
                    ['Generated on: ' + new Date().toLocaleString()],
                    ['Time Range: ' + (dateRangePreset || 'Selected Period')],
                    ['Total Events: ' + truckData.filter(e => !e.isAQIOnly).length], // count only truck events & filter out interval entries
                    [''],
                ], { origin: 'A1' });

                XLSX.utils.book_append_sheet(wb, ws, "Truck Events");

                XLSX.writeFile(wb, `truck-events-${new Date().toISOString().split('T')[0]}.xlsx`);
            } else if (format === 'csv') {
                const header = [
                    'Trucks Off Our Streets Dashboard',
                    'Generated on: ' + new Date().toLocaleString(),
                    'Time Range: ' + (dateRangePreset || 'Selected Period'),
                    'Total Events: ' + truckData.filter(e => !e.isAQIOnly).length, // count only truck events & filter out interval entries
                    '',
                ];

                const ws = XLSX.utils.json_to_sheet(dataToExport);
                const csv = XLSX.utils.sheet_to_csv(ws);

                const fullCsv = header.join('\n') + '\n' + csv;

                const data = new Blob([fullCsv], { type: 'text/csv;charset=utf-8' });
                saveAs(data, `truck-events-${new Date().toISOString().split('T')[0]}.csv`);
            }
        } catch (error) {
            console.error(`Error exporting to ${format.toUpperCase()}:`, error);
            alert(`Failed to export to ${format.toUpperCase()}. Please try again.`);
        }
    };

    const togglePollutants = (eventId) => {
        setTimeout(() => {
            setExpandedPollutants(prev => ({
                ...prev,
                [eventId]: !prev[eventId]
            }));
        }, 50);
    };

    useEffect(() => {
        const updateTimeSinceRefresh = () => {
            if (lastRefresh) {
                const now = new Date();
                const diffMinutes = Math.round((now - lastRefresh) / 60000);
                setTimeSinceLastRefresh(diffMinutes);
            }
        };

        updateTimeSinceRefresh();

        const interval = setInterval(updateTimeSinceRefresh, 60000);

        return () => clearInterval(interval);
    }, [lastRefresh]);

    // Auto-switch view mode when date range changes
    useEffect(() => {
        // If Last 24 hrs is selected and current view is weekly or monthly, switch to daily
        if (dateRangePreset === '24h' && (viewMode === 'weekly' || viewMode === 'monthly')) {
            setViewMode('daily');
        }
        // If Last 72 hrs is selected and current view is weekly or monthly, switch to daily
        if (dateRangePreset === '72h' && (viewMode === 'weekly' || viewMode === 'monthly')) {
            setViewMode('daily');
        }
        // If Last 7 Days is selected and current view is monthly, switch to daily
        if (dateRangePreset === 'week' && viewMode === 'monthly') {
            setViewMode('daily');
        }
    }, [dateRangePreset, viewMode]);

    const getCacheStatus = () => {
        if (!lastRefresh) return null;
        
        if (timeSinceLastRefresh < 1) {
            return { color: 'green-500', text: 'Fresh Data' };
        } else if (timeSinceLastRefresh < 5) {
            return { color: 'green-500', text: `${timeSinceLastRefresh} min ago` };
        } else if (timeSinceLastRefresh < 10) {
            return { color: 'yellow-500', text: `${timeSinceLastRefresh} min ago` };
        } else {
            return { color: 'red-500', text: `${timeSinceLastRefresh} min ago` };
        }
    };

    const getCurrentPeriodSummary = () => {
        if (!aggregatedData || !Array.isArray(aggregatedData) || aggregatedData.length === 0) return null;

        const totalTrucks = aggregatedData.reduce((sum, period) => sum + (period.truckCount || 0), 0);
        const validAQIPeriods = aggregatedData.filter(period => period.avgAQI > 0);
        let avgAQI = 0;
        
        if (validAQIPeriods.length > 0) {
            const totalAQI = validAQIPeriods.reduce((sum, period) => sum + period.avgAQI, 0);
            avgAQI = Math.round(totalAQI / validAQIPeriods.length);
        }

        return {
            trucks: totalTrucks,
            avgAQI,
            totalEvents: aggregatedData.length
        };
    };

    const toggleDarkMode = () => { // eslint-disable-line no-unused-vars
        setDarkMode(!darkMode);
        document.documentElement.classList.toggle('dark');
    };

    const graphOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        elements: {
            line: {
                tension: 0.3,
                borderWidth: 2
            },
            point: {
                radius: 2,
                hitRadius: 8,
                hoverRadius: 4,
                borderWidth: 2
            }
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: 15,
                    font: {
                        size: 12,
                        weight: 'bold'
                    },
                    color: darkMode ? '#fff' : '#1f2937'
                }
            },
            tooltip: {
                backgroundColor: darkMode ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                titleColor: darkMode ? '#fff' : '#1f2937',
                bodyColor: darkMode ? '#fff' : '#1f2937',
                borderColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                borderWidth: 1,
                padding: 12,
                cornerRadius: 8,
                usePointStyle: true,
                callbacks: {
                    title: function(context) {
                        const label = context[0].label;
                        if (label.startsWith('Week of')) {
                            return label; // Already formatted
                        }
                        // Format the date based on view mode
                        switch (viewMode) {
                            case 'hourly':
                                return new Date(label).toLocaleString();
                            case 'daily':
                                return new Date(label).toLocaleDateString();
                            case 'weekly':
                                return label;
                            case 'monthly':
                                return label;
                            default:
                        return label;
                    }
                    },
                    label: function(context) {
                        const label = context.dataset.label;
                        const value = context.parsed.y;
                        if (label === 'Truck Events') {
                            return `${label}: ${value} trucks`;
                        } else if (label === 'AQI') {
                            return `${label}: ${value?.toFixed(1) || 'No data'}`;
                        }
                        return `${label}: ${value}`;
                    }
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: true,
                    drawBorder: false,
                    color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                    autoSkip: true,
                    maxTicksLimit: viewMode === 'monthly' ? 12 : viewMode === 'weekly' ? 10 : 20,
                    font: {
                        size: 11
                    },
                    color: darkMode ? '#fff' : '#1f2937'
                }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Truck Events',
                    font: {
                        size: 12,
                        weight: 'bold'
                    },
                    color: darkMode ? '#fff' : '#1f2937'
                },
                grid: {
                    color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    drawBorder: false
                },
                ticks: {
                    beginAtZero: true,
                    precision: 0,
                    font: {
                        size: 11
                    },
                    color: darkMode ? '#fff' : '#1f2937',
                    callback: function(value) {
                        return Math.round(value);
                    }
                },
                min: 0
            },
            y2: {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                    display: true,
                    text: 'AQI Values',
                    font: {
                        size: 12,
                        weight: 'bold'
                    },
                    color: darkMode ? '#fff' : '#1f2937'
                },
                grid: {
                    drawOnChartArea: false,
                    color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                },
                ticks: {
                    beginAtZero: true,
                    font: {
                        size: 11
                    },
                    color: darkMode ? '#fff' : '#1f2937',
                    callback: function(value) {
                        return Math.round(value);
                    }
                },
                min: 0
            }
        }
    };

    const LoadingSkeleton = () => (
        <div className="space-y-6">
            {(loading || isRefreshing) && progress > 0 && (
                <div className="progress-container bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-blue-100 dark:border-gray-700">
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Loading Truck Events</h3>
                        <span className="text-sm font-medium text-blue-600 dark:text-blue-400">{progress.toFixed(1)}%</span>
                    </div>
                    <div className="progress-bar bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                        <div 
                            className="progress-fill bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full transition-all duration-300 ease-out" 
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        Streaming data from server... This may take a moment for large date ranges.
                    </p>
                </div>
            )}
            
            <div className="animate-pulse">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                <div className="space-y-3">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                </div>
            </div>
        </div>
    );

    const handleCompareData = async () => { // eslint-disable-line no-unused-vars
        if (!selectedCompareRange) return;
        
        try {
            const endDate = new Date();
            const startDate = new Date();
            const days = parseInt(selectedCompareRange);
            
            endDate.setDate(endDate.getDate() - days);
            startDate.setDate(startDate.getDate() - (days * 2));
            
            const formattedStartDate = startDate.toISOString();
            const formattedEndDate = endDate.toISOString();
            
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/trucks/${selectedCameraId}?startDate=${encodeURIComponent(formattedStartDate)}&endDate=${encodeURIComponent(formattedEndDate)}`
            );
            
            if (Array.isArray(response.data)) {
                if (viewMode === 'list') {
                    handleViewModeChange('daily');
                }
                setCompareMode(true);
            }
        } catch (error) {
            console.error('Failed to fetch comparison data:', error);
            alert("Failed to fetch comparison data. Please try again.");
        }
    };

    // Update the list view rendering to show loading state
    const renderList = () => {
        if (loading) {
            return <LoadingSkeleton />;
        }

        if (errorStates.fetchData) {
    return (
                <div className="text-center py-12">
                    <div className="text-red-500">{errorStates.fetchData}</div>
                    <p className="text-sm text-gray-500 mt-2">Please try again or contact support if the issue persists</p>
                </div>
            );
        }

        if (!truckData || truckData.length === 0) {
            return (
                <div className="text-center py-12">
                    <div className="text-gray-400">No truck events available</div>
                    <p className="text-sm text-gray-500 mt-2">
                        {selectedCameraId ? 'No events found for the selected filters' : 'Select a camera and fetch data to view events'}
                    </p>
                </div>
            );
        }

        // Filter the existing data based on event type
        const filteredData = truckData.filter(event => {
            if (filter.eventType === 'all') return true;
            if (filter.eventType === 'truck') return !event.isAQIOnly;
            if (filter.eventType === 'aqi') return event.isAQIOnly;
            return true;
        });

        // Update counts based on filtered data
        const counts = {
            truckEvents: filteredData.filter(event => !event.isAQIOnly).length,
            aqiOnly: filteredData.filter(event => event.isAQIOnly).length,
            total: filteredData.length
        };

        return (
            <div className="space-y-6">
                <div className="flex flex-col gap-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Events</h2>
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <button onClick={() => setEventFilterMenuOpen(prev => !prev)} className="bg-white/90 dark:bg-gray-700/60 backdrop-blur-lg border border-gray-200 dark:border-white/20 text-gray-600 dark:text-gray-200 px-4 py-2 rounded-xl text-sm hover:bg-white dark:hover:bg-gray-700/80 transition-all duration-300 shadow-lg">
                                    {filter.eventType === 'all' ? 'All Events' :
                                     filter.eventType === 'truck' ? 'Truck Events Only' :
                                     'AQI Events Only'}
                                </button>
                                {eventFilterMenuOpen && (
                                    <>
                                        <div className="fixed inset-0 z-[99]" onClick={() => setEventFilterMenuOpen(false)} />
                                        <div className="absolute right-0 mt-1 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl shadow-lg border border-white/20 p-1 min-w-[200px] z-[100]">
                                            <button onClick={() => { setFilter(prev => ({ ...prev, eventType: 'all' })); setEventFilterMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-white rounded-lg hover:bg-blue-500/10 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-all duration-200">
                                                All Events
                                            </button>
                                            <button onClick={() => { setFilter(prev => ({ ...prev, eventType: 'truck' })); setEventFilterMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-500/10 hover:text-blue-700 dark:hover:text-blue-500 cursor-pointer transition-all duration-200">
                                                Truck Events Only
                                            </button>
                                            <button onClick={() => { setFilter(prev => ({ ...prev, eventType: 'aqi' })); setEventFilterMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-500/10 hover:text-gray-700 dark:hover:text-gray-500 cursor-pointer transition-all duration-200">
                                                AQI Events Only
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <div className="bg-blue-50 px-4 py-2 rounded-xl border border-blue-100">
                                    <span className="text-blue-700 font-semibold">
                                        {counts.truckEvents} Truck {counts.truckEvents === 1 ? 'Event' : 'Events'}
                                    </span>
                                </div>
                                <div className="bg-gray-50 px-4 py-2 rounded-xl border border-gray-100">
                                    <span className="text-gray-700 font-semibold">
                                        {counts.aqiOnly} AQI {counts.aqiOnly === 1 ? 'Reading' : 'Readings'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="divide-y divide-gray-100">
                    {filteredData.map((event, index) => (
                        <div 
                            key={`${event.id}-${index}`} 
                            className={`py-6 first:pt-0 last:pb-0 transition-all duration-500 ${
                                event.isNew ? 'animate-fade-in' : ''
                            }`}
                        >
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                {formatEventId(event.id, event.eagleEyeObjectId, event.startTimestamp)}
                                            </div>
                                            {!event.isAQIOnly && (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    Truck Event
                                                </span>
                                            )}
                                            {event.isAQIOnly && (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                    AQI Only
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1">
                                            {new Date(event.startTimestamp).toLocaleString()}
                                        </div>
                                    </div>
                                    {event.aqiData && (
                                        <div className="w-64">
                                            <AQIIndicator aqi={event.aqiData.aqi} />
                                        </div>
                                    )}
                                </div>

                                {event.monitorInfo && (
                                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="text-sm text-gray-500">
                                                <span className="font-medium text-gray-900 dark:text-white">Monitor:</span> {event.monitorInfo.code}
                                            </div>
                                            {event.monitorInfo.distance && (
                                                <div className="text-sm text-gray-500">
                                                    <span className="font-medium text-gray-900 dark:text-white">Distance:</span>{' '}
                                                    {(parseFloat(event.monitorInfo.distance) * 0.000621371).toFixed(2)} miles
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {event.aqiData && (
                                    <div>
                                        <button
                                            onClick={() => togglePollutants(event.id)}
                                            className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                                        >
                                            {expandedPollutants[event.id] ? 'Hide' : 'Show'} Pollutant Details
                                        </button>
                                        <div
                                            className={`mt-2 overflow-hidden transition-all duration-300 ${
                                                expandedPollutants[event.id] ? 'max-h-96' : 'max-h-0'
                                            }`}
                                        >
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {event.aqiData.pm25 !== undefined && (
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                                                        <div className="text-sm font-medium text-gray-500">PM2.5</div>
                                                        <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                                                            {event.aqiData.pm25} µg/m³
                                                        </div>
                                                    </div>
                                                )}
                                                {event.aqiData.no2 !== undefined && (
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                                                        <div className="text-sm font-medium text-gray-500">NO₂</div>
                                                        <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                                                            {event.aqiData.no2} PPB
                                                        </div>
                                                    </div>
                                                )}
                                                {event.aqiData.pm10 !== undefined && (
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                                                        <div className="text-sm font-medium text-gray-500">PM10</div>
                                                        <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                                                            {event.aqiData.pm10} µg/m³
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {!event.isAQIOnly && (
                                    <div>
                                        <button
                                            onClick={() => fetchSnapshot(event.id, event.startTimestamp)}
                                            className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                                            disabled={loadingSnapshots[event.id]}
                                        >
                                            {loadingSnapshots[event.id] ? 'Loading...' : visibleSnapshots[event.id] ? 'Hide' : 'Show'} Snapshot
                                        </button>
                                        {errorStates[event.id] && (
                                            <p className="mt-2 text-sm text-red-500">{errorStates[event.id]}</p>
                                        )}
                                        <div
                                            className={`mt-2 overflow-hidden transition-all duration-300 ${
                                                visibleSnapshots[event.id] ? 'max-h-[500px]' : 'max-h-0'
                                            }`}
                                        >
                                            {snapshots[event.id] && (
                                                <img
                                                    src={snapshots[event.id]}
                                                    alt={`Snapshot for event ${event.id}`}
                                                    className="w-full h-auto rounded-lg shadow-lg"
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}

                                {index > 0 && (
                                    <div className="mt-2">
                                        <button
                                            onClick={() => handleOpenModal(index)}
                                            className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                                        >
                                            Compare with Previous Event
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                {isLoadingMore && (
                    <div className="text-center p-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white mx-auto"></div>
                    </div>
                )}
            </div>
        );
    };

    // Add function to handle hotspot clicks
    const handleHotspotClick = async () => {
        const newClicks = hotspotClicks + 1;
        setHotspotClicks(newClicks);

        if (newClicks === 3) {
            try {
                // Get token info from localStorage
                const accessToken = localStorage.getItem('accessToken');
                const refreshToken = localStorage.getItem('refreshToken');
                const tokenExpiry = localStorage.getItem('tokenExpiry');
                
                setTokenInfo({
                    accessToken: accessToken ? `${accessToken.slice(0, 10)}...` : 'Not found',
                    refreshToken: refreshToken ? `${refreshToken.slice(0, 10)}...` : 'Not found',
                    tokenExpiry: tokenExpiry ? new Date(parseInt(tokenExpiry)).toLocaleString() : 'Not found'
                });
                setShowTokenInfo(true);
            } catch (error) {
                console.error('Error getting token info:', error);
            }
        }

        // Reset clicks after 5 seconds
        setTimeout(() => {
            setHotspotClicks(0);
        }, 5000);
    };

    return (
        <div className={`min-h-screen ${darkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-gray-100 to-gray-200'}`}>
            <nav className={`${darkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-md fixed w-full z-50 shadow-md transition-all duration-300`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div 
                            onClick={() => navigate('/')} 
                            className="cursor-pointer hover:opacity-80 transition-opacity duration-200"
                        >
                            <JustAirLogo className="h-8 w-auto" />
                        </div>
                        <h1 className={`${darkMode ? 'text-white' : 'text-gray-900'} text-lg font-semibold`}>
                            Trucks Off Our Streets Dashboard
                        </h1>
                    </div>
                    <div className="flex items-center gap-4">
                        {lastRefresh && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span>Last updated: {lastRefresh.toLocaleTimeString()}</span>
                                {isRefreshing && (
                                    <div className="flex items-center gap-1 text-blue-600">
                                        <Spinner className="w-4 h-4" />
                                        <span>Streaming...</span>
                                    </div>
                                )}
                                {getCacheStatus() && (
                                    <div className="flex items-center gap-1">
                                        <span className={`w-2 h-2 rounded-full bg-${getCacheStatus().color}`}></span>
                                        <span className={`text-${getCacheStatus().color}`}>{getCacheStatus().text}</span>
                                    </div>
                                )}
                            </div>
                        )}
                        <button
                            onClick={() => exportData('xlsx')}
                            className="bg-gradient-to-br from-green-500 to-green-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg backdrop-blur-lg border border-white/10"
                        >
                            Export to Excel
                        </button>
                        <button
                            onClick={() => exportData('csv')}
                            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg backdrop-blur-lg border border-white/10"
                        >
                            Export to CSV
                        </button>
                    </div>
                </div>
            </nav>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-8">
                {/* Only show view mode buttons after data has been fetched */}
                {truckData.length > 0 && (
                    <div className="mb-6 bg-white/60 backdrop-blur-xl rounded-2xl shadow-lg p-4 border border-white/20">
                        <div className="flex gap-2">
                            {['daily', 'weekly', 'monthly', 'list']
                                .filter(mode => {
                                    // Hide weekly and monthly views when Last 24 hrs is selected
                                    if (dateRangePreset === '24h' && (mode === 'weekly' || mode === 'monthly')) {
                                        return false;
                                    }
                                    // Hide weekly and monthly views when Last 72 hrs is selected
                                    if (dateRangePreset === '72h' && (mode === 'weekly' || mode === 'monthly')) {
                                        return false;
                                    }
                                    // Hide monthly view when Last 7 Days is selected
                                    if (dateRangePreset === 'week' && mode === 'monthly') {
                                        return false;
                                    }
                                    return true;
                                })
                                .map((mode) => (
                                <button
                                    key={mode}
                                    onClick={() => handleViewModeChange(mode)}
                                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${
                                        viewMode === mode
                                            ? 'bg-blue-500 text-white'
                                            : 'bg-white/60 text-gray-700 hover:bg-blue-50'
                                    }`}
                                >
                                    {mode.charAt(0).toUpperCase() + mode.slice(1)} View
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-3">
                        <div className={`${darkMode ? 'bg-gray-800/60' : 'bg-white/60'} backdrop-blur-xl rounded-2xl shadow-lg p-6 sticky top-24 border border-white/20`}>
                            <div className="space-y-6">
                                <div className="relative">
                                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                                        Camera
                                    </label>
                                    {errorStates.fetchCameras ? (
                                        <div className="text-red-500 text-sm mb-2">{errorStates.fetchCameras}</div>
                                    ) : null}
                                    <div className="relative">
                                        <button onClick={() => setCameraMenuOpen(prev => !prev)} className="w-full bg-white/90 dark:bg-gray-700/60 backdrop-blur-lg border border-gray-200 dark:border-white/20 text-gray-600 dark:text-gray-200 px-4 py-2.5 rounded-xl text-sm hover:bg-white dark:hover:bg-gray-700/80 transition-all duration-300 shadow-lg text-left">
                                            {selectedCameraId ? (cameras.find(cam => cam.id === selectedCameraId)?.name || 'Select Camera') : 'Select Camera'}
                                        </button>
                                        {cameraMenuOpen && (
                                            <>
                                                <div className="fixed inset-0 z-[99]" onClick={() => setCameraMenuOpen(false)} />
                                                <div className="absolute left-0 mt-1 w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl shadow-lg border border-white/20 p-1 min-w-[200px] z-[100]">
                                                    {cameras.length > 0 ? (
                                                        cameras.map((camera) => (
                                                            <button
                                                                key={camera.id}
                                                                onClick={() => { setSelectedCameraId(camera.id); setCameraMenuOpen(false); }}
                                                                className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-white rounded-lg hover:bg-blue-500/10 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-all duration-200"
                                                            >
                                                                {camera.name || `Camera ${camera.id}`}
                                                            </button>
                                                        ))
                                                    ) : (
                                                        <div className="px-3 py-2 text-sm text-gray-500">
                                                            No cameras available
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="relative">
                                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                                        Date Range
                                    </label>
                                    <div className="relative">
                                        <button onClick={() => setDateRangeMenuOpen(prev => !prev)} className="w-full bg-white/90 dark:bg-gray-700/60 backdrop-blur-lg border border-gray-200 dark:border-white/20 text-gray-600 dark:text-gray-200 px-4 py-2.5 rounded-xl text-sm hover:bg-white dark:hover:bg-gray-700/80 transition-all duration-300 shadow-lg text-left">
                                            {dateRangePreset === 'custom' && customStartDate && customEndDate
                                                ? `Custom: ${customStartDate.toLocaleDateString()} - ${customEndDate.toLocaleDateString()}`
                                                : dateRangePresets.find(range => range.value === dateRangePreset)?.label || 'Select Range'}
                                        </button>
                                        {dateRangeMenuOpen && (
                                            <>
                                                <div className="fixed inset-0 z-[99]" onClick={() => setDateRangeMenuOpen(false)} />
                                                <div className="absolute left-0 mt-1 w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl shadow-lg border border-white/20 p-1 min-w-[200px] z-[100]">
                                                    {dateRangePresets.map((range) => (
                                                        <button
                                                            key={range.value}
                                                            onClick={() => { setDateRangePreset(range.value); setDateRangeMenuOpen(false); }}
                                                            className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-white rounded-lg hover:bg-blue-500/10 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-all duration-200"
                                                        >
                                                            {range.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    
                                    {dateRangePreset === 'custom' && (
                                        <div className="mt-4 space-y-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-200 dark:border-gray-600">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                                Select Custom Date Range
                                            </div>
                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                <div className="grid grid-cols-1 gap-3">
                                                    <DatePicker
                                                        label="Start Date"
                                                        value={customStartDate}
                                                        onChange={(newValue) => handleCustomDateChange(newValue, customEndDate)}
                                                        maxDate={new Date()}
                                                        minDate={(() => {
                                                            const oneYearAgo = new Date();
                                                            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                                                            return oneYearAgo;
                                                        })()}
                                                        renderInput={(params) => (
                                                            <TextField 
                                                                {...params} 
                                                                size="small"
                                                                className="bg-white dark:bg-gray-600 rounded-lg"
                                                            />
                                                        )}
                                                    />
                                                    <DatePicker
                                                        label="End Date"
                                                        value={customEndDate}
                                                        onChange={(newValue) => handleCustomDateChange(customStartDate, newValue)}
                                                        maxDate={new Date()}
                                                        minDate={customStartDate || (() => {
                                                            const oneYearAgo = new Date();
                                                            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                                                            return oneYearAgo;
                                                        })()}
                                                        renderInput={(params) => (
                                                            <TextField 
                                                                {...params} 
                                                                size="small"
                                                                className="bg-white dark:bg-gray-600 rounded-lg"
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </LocalizationProvider>
                                            
                                            {customDateError && (
                                                <div className="text-sm text-red-500 mt-2">
                                                    {customDateError}
                                                </div>
                                            )}
                                            
                                            {customStartDate && customEndDate && !customDateError && (
                                                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
                                                    <div className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                                                        Selected Range:
                                                    </div>
                                                    <div className="text-sm text-blue-700 dark:text-blue-200">
                                                        {customStartDate.toLocaleDateString()} - {customEndDate.toLocaleDateString()}
                                                        <br />
                                                        <span className="text-xs text-blue-600 dark:text-blue-300">
                                                            ({Math.ceil((customEndDate - customStartDate) / (1000 * 60 * 60 * 24))} days)
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                </div>

                                <button
                                    onClick={() => memoizedFetchTruckData()}
                                    disabled={loading || isRefreshing || (dateRangePreset === 'custom' && customDateError)}
                                    className={`w-full bg-gradient-to-br from-blue-500 to-blue-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 shadow-lg backdrop-blur-lg border border-white/10 ${
                                        (loading || isRefreshing || (dateRangePreset === 'custom' && customDateError)) ? 'opacity-75 cursor-not-allowed' : 'hover:from-blue-600 hover:to-blue-700'
                                    }`}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        {(loading || isRefreshing) && <Spinner className="w-4 h-4" />}
                                        <span>{loading ? 'Streaming Data...' : isRefreshing ? 'Streaming...' : 'Stream Data'}</span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-9">
                        <div className={`${darkMode ? 'bg-gray-800/60' : 'bg-white/60'} backdrop-blur-xl rounded-2xl shadow-lg p-6`}>
                            {loading ? (
                                <LoadingSkeleton />
                            ) : viewMode === 'list' ? (
                                renderList()
                            ) : (
                                    <div className="space-y-6">
                                        <div className="flex justify-between items-center">
                                        <h2 className="text-xl font-semibold text-gray-900">
                                            {viewMode.charAt(0).toUpperCase() + viewMode.slice(1)} Analysis
                                        </h2>
                                        {getCurrentPeriodSummary() && (
                                            <div className="flex gap-4">
                                            <div className="bg-blue-50 px-4 py-2 rounded-xl border border-blue-100">
                                                <span className="text-blue-700 font-semibold">
                                                        {getCurrentPeriodSummary().trucks} {getCurrentPeriodSummary().trucks === 1 ? 'Truck' : 'Trucks'}
                                                </span>
                                            </div>
                                                <div className="bg-orange-50 px-4 py-2 rounded-xl border border-orange-100">
                                                    <span className="text-orange-700 font-semibold">
                                                        Avg AQI: {getCurrentPeriodSummary().avgAQI}
                                                                    </span>
                                                                </div>
                                                                </div>
                                        )}
                                                                </div>
                                    <div className="h-96">
                                        {aggregatedData && aggregatedData.length > 0 ? (
                                            <Line
                                                data={{
                                                    labels: aggregatedData.map(data => data.timestamp),
                                                    datasets: [
                                                        {
                                                            label: 'Truck Events',
                                                            data: aggregatedData.map(data => data.truckCount),
                                                            borderColor: darkMode ? 'rgb(96, 165, 250)' : 'rgb(59, 130, 246)',
                                                            backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.5)' : 'rgba(59, 130, 246, 0.5)',
                                                            yAxisID: 'y',
                                                            order: 1
                                                        },
                                                        {
                                                            label: 'AQI',
                                                            data: aggregatedData.map(data => data.avgAQI),
                                                            borderColor: darkMode ? 'rgb(251, 146, 60)' : 'rgb(249, 115, 22)',
                                                            backgroundColor: darkMode ? 'rgba(251, 146, 60, 0.5)' : 'rgba(249, 115, 22, 0.5)',
                                                            yAxisID: 'y2',
                                                            order: 2
                                                        }
                                                    ]
                                                }}
                                                options={graphOptions}
                                                key={`${viewMode}-${darkMode}-${compareMode}`}
                                            />
                                        ) : (
                                            <div className="flex items-center justify-center h-full">
                                                <p className="text-gray-500">No data available for the selected period</p>
                                                                </div>
                                        )}
                                                            </div>
                                                                        </div>
                            )}
                                                            </div>
                                                        </div>
                                                                    </div>
                                                                    </div>

            <AQIModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} currentEvent={currentEvent} previousEvent={previousEvent} />

            {/* Add secret hotspot */}
            <div 
                className="fixed bottom-4 left-4 w-8 h-8 cursor-pointer opacity-0 hover:opacity-100 transition-opacity duration-200"
                onClick={handleHotspotClick}
            >
                <div className="w-full h-full rounded-full bg-blue-500/20"></div>
                                                            </div>

            {/* Add token info modal */}
            {showTokenInfo && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Token Information</h3>
                                                <button
                                onClick={() => setShowTokenInfo(false)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                                </button>
                                                </div>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Access Token</label>
                                <div className="mt-1 text-sm text-gray-900 dark:text-white font-mono break-all">
                                    {tokenInfo?.accessToken}
                                            </div>
                                    </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Refresh Token</label>
                                <div className="mt-1 text-sm text-gray-900 dark:text-white font-mono break-all">
                                    {tokenInfo?.refreshToken}
                                    </div>
                                                    </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Token Expiry</label>
                                <div className="mt-1 text-sm text-gray-900 dark:text-white">
                                    {tokenInfo?.tokenExpiry}
                                                    </div>
                                                </div>
                                        </div>
                                        </div>
                                    </div>
            )}
            
            {dataRangeWarning && (
                <div className="fixed bottom-4 right-4 max-w-md bg-amber-50 border-l-4 border-amber-400 p-4 rounded-lg shadow-lg z-50 animate-fade-in">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-amber-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-amber-700">
                                {dataRangeWarning}
                            </p>
                        </div>
                        <div className="ml-auto pl-3">
                            <div className="-mx-1.5 -my-1.5">
                                <button
                                    onClick={() => setDataRangeWarning(null)}
                                    className="inline-flex rounded-md p-1.5 text-amber-500 hover:bg-amber-100 focus:outline-none"
                                >
                                    <span className="sr-only">Dismiss</span>
                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const fadeInAnimation = `
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
.animate-fade-in {
    animation: fadeIn 0.5s ease-out forwards;
}
`;

const style = document.createElement('style');
style.textContent = fadeInAnimation;
document.head.appendChild(style);

export default JobList; 